import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const SECTIONS = [
  { key:'daily_verse',  label:"Today's Bible Verse",           icon:'📖' },
  { key:'about',        label:'Our Vision, Mission & Core Values', icon:'✝' },
  { key:'pastors',      label:'Meet Our Pastors',              icon:'👨‍💼' },
  { key:'services',     label:'Service Times',                 icon:'🕐' },
  { key:'events',       label:'Upcoming Events',               icon:'📅' },
  { key:'sermons',      label:'Recent Sermons',                icon:'🎤' },
  { key:'gallery',      label:'Church Gallery',                icon:'🖼️' },
  { key:'testimonials', label:'Church Testimonials',           icon:'✦' },
  { key:'branches',     label:'Branch Churches',               icon:'⛪' },
  { key:'prayer',       label:'Prayer Request',                icon:'🙏' },
  { key:'give',         label:"Give to God's Work",            icon:'💳' },
  { key:'connect',      label:'Follow Us Online',              icon:'📱' },
];

export default function SectionsTab() {
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    supabase.from('section_visibility').select('key,enabled')
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach(r => { map[r.key] = r.enabled; });
          // Default true for any missing
          SECTIONS.forEach(s => { if (map[s.key] === undefined) map[s.key] = true; });
          setSections(map);
        } else {
          const def = {};
          SECTIONS.forEach(s => { def[s.key] = true; });
          setSections(def);
        }
      });
  }, []);

  const toggle = async (key) => {
    const newVal = !sections[key];
    setSaving(key);
    setSections(prev => ({ ...prev, [key]: newVal }));

    // Use UPDATE not upsert — rows already exist from SQL setup
    const { error } = await supabase
      .from('section_visibility')
      .update({ enabled: newVal })
      .eq('key', key);

    setSaving('');
    if (error) {
      // Revert on failure
      setSections(prev => ({ ...prev, [key]: !newVal }));
      setMsg(`❌ Failed to save. Run sections_setup.sql in Supabase first.`);
    } else {
      setMsg(`${SECTIONS.find(s=>s.key===key)?.label} — ${newVal ? 'Enabled ✅' : 'Disabled ❌'}`);
    }
    setTimeout(() => setMsg(''), 4000);
  };

  const enableAll = async () => {
    const all = {};
    SECTIONS.forEach(s => { all[s.key] = true; });
    setSections(all);
    await Promise.all(SECTIONS.map(s =>
      supabase.from('section_visibility').update({ enabled: true }).eq('key', s.key)
    ));
    setMsg('All sections enabled ✅');
    setTimeout(() => setMsg(''), 3000);
  };

  const enabledCount = Object.values(sections).filter(Boolean).length;

  return (
    <div>
      <div style={{ background:'rgba(184,134,11,0.08)', border:'1px solid #d4b896', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:'0.82rem', color:'#5a4e3e', lineHeight:1.6 }}>
        👁️ <strong style={{ color:'#8b6508' }}>Section Visibility:</strong> Toggle any section ON or OFF. Disabled sections are hidden from the website instantly. All content is preserved — just hidden from visitors.
      </div>

      {msg && (
        <div style={{ background:'#f0fff0', border:'1px solid #5cb85c', borderRadius:8, padding:'10px 16px', marginBottom:16, fontSize:'0.85rem', color:'#1d5c1d' }}>
          ✅ {msg}
        </div>
      )}

      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'0.82rem', color:'#6b5e4e' }}>
          {enabledCount} of {SECTIONS.length} sections visible
        </span>
        <button onClick={enableAll} style={{ background:'#f0ebe0', border:'1px solid #d4b896', color:'#6b5e4e', padding:'7px 16px', borderRadius:8, cursor:'pointer', fontSize:'0.76rem', fontWeight:700, fontFamily:"'Raleway',sans-serif", marginLeft:'auto' }}>
          ✅ Enable All
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:12 }}>
        {SECTIONS.map(sec => {
          const on = sections[sec.key] !== false;
          return (
            <div key={sec.key} style={{
              background:'#fff', border:`2px solid ${on ? 'rgba(92,184,92,0.4)' : 'rgba(178,34,34,0.25)'}`,
              borderRadius:12, padding:'16px 18px',
              display:'flex', alignItems:'center', justifyContent:'space-between',
              boxShadow:'0 2px 8px rgba(0,0,0,0.04)',
              opacity: saving === sec.key ? 0.7 : 1,
              transition:'all 0.2s',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{
                  width:40, height:40, borderRadius:10,
                  background: on ? 'rgba(92,184,92,0.1)' : 'rgba(178,34,34,0.08)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'1.2rem', flexShrink:0,
                }}>
                  {sec.icon}
                </div>
                <div>
                  <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.72rem', color:'#1a2a4a', marginBottom:3 }}>
                    {sec.label}
                  </div>
                  <div style={{ fontSize:'0.66rem', color: on ? '#1d5c1d' : '#8b0000', fontWeight:700, letterSpacing:'0.08em' }}>
                    {on ? '● VISIBLE ON WEBSITE' : '● HIDDEN FROM WEBSITE'}
                  </div>
                </div>
              </div>
              {/* Toggle switch */}
              <div
                onClick={() => toggle(sec.key)}
                style={{
                  width:52, height:28, borderRadius:14, cursor:'pointer',
                  background: on ? '#5cb85c' : '#ccc',
                  position:'relative', transition:'background 0.25s', flexShrink:0,
                }}
              >
                <div style={{
                  position:'absolute', top:4,
                  left: on ? 28 : 4,
                  width:20, height:20, borderRadius:'50%',
                  background:'#fff', transition:'left 0.25s',
                  boxShadow:'0 2px 4px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop:20, background:'#fff', border:'1px solid #e8d8b0', borderRadius:10, padding:'14px 18px', fontSize:'0.78rem', color:'#6b5e4e', lineHeight:1.7 }}>
        <strong style={{ color:'#8b6508' }}>💡 How it works:</strong> Toggle saves instantly to database. Visitors will see the change immediately when they refresh the website. Content is NOT deleted — it can be re-enabled anytime.
      </div>
    </div>
  );
}
